import { useMemo } from 'react';

import useJurisdictions from '@/hooks/useJurisdictions';
import { DjangoOperator, LogicGroup, LogicOperator } from '@/types/logic';
import {
  elementKey,
  getSelectedJurisdictionIds,
  isJurisdictionGroup,
  jurisdictionClassificationKey,
  JURISDICTION_ELIGIBILITY_FIELD
} from '@/utils/jurisdictionEligibility';

export { JURISDICTION_ELIGIBILITY_FIELD };

type Builder = {
  rootGroups: LogicGroup[];
  onAddRootGroup: () => string;
  onUpdateGroup: (groupId: string, data: { logic_operator?: LogicOperator }) => void;
  onDeleteGroup: (groupId: string) => void;
  onAddElement: (
    groupId: string,
    defaults: { field: string; comparison_operator: string; value: unknown }
  ) => void;
  onDeleteElement: (elementId: string) => void;
};

/**
 * Two-way binds a JurisdictionSelect (jurisdiction ids) to a dedicated "Any
 * of" root group in the eligibility builder's tree: one LogicElement per
 * selected jurisdiction's classification key, OR'd together. Reads/writes
 * through whichever root group already looks like a jurisdiction-only OR
 * group, so it also picks up criteria loaded from a previously-saved tree.
 */
export const useJurisdictionEligibility = (builder: Builder) => {
  const { data: jurisdictions } = useJurisdictions();

  const idToKey = useMemo(() => {
    const map = new Map<string, string>();
    (jurisdictions ?? []).forEach((jurisdiction) =>
      map.set(jurisdiction.id, jurisdictionClassificationKey(jurisdiction.name))
    );
    return map;
  }, [jurisdictions]);

  const jurisdictionGroup = builder.rootGroups.find(isJurisdictionGroup);

  const existingKeys = useMemo(
    () =>
      jurisdictionGroup?.logic_elements
        .map(elementKey)
        .filter((key): key is string => key !== null) ?? [],
    [jurisdictionGroup]
  );

  const selectedJurisdictionIds = useMemo(
    () => getSelectedJurisdictionIds(builder.rootGroups, jurisdictions ?? []),
    [builder.rootGroups, jurisdictions]
  );

  const onChangeSelectedJurisdictionIds = (jurisdictionIds: string[]) => {
    const nextKeys = new Set(
      jurisdictionIds.map((id) => idToKey.get(id)).filter((key): key is string => !!key)
    );

    if (nextKeys.size === 0) {
      if (jurisdictionGroup) builder.onDeleteGroup(jurisdictionGroup.id);
      return;
    }

    jurisdictionGroup?.logic_elements
      .filter((element) => !nextKeys.has(elementKey(element) ?? ''))
      .forEach((element) => builder.onDeleteElement(element.id));

    const keysToAdd = [...nextKeys].filter((key) => !existingKeys.includes(key));
    if (keysToAdd.length === 0) return;

    const groupId = jurisdictionGroup?.id ?? builder.onAddRootGroup();
    if (!jurisdictionGroup) builder.onUpdateGroup(groupId, { logic_operator: LogicOperator.OR });

    keysToAdd.forEach((key) =>
      builder.onAddElement(groupId, {
        field: JURISDICTION_ELIGIBILITY_FIELD,
        comparison_operator: DjangoOperator.CONTAINS,
        value: [key]
      })
    );
  };

  return { selectedJurisdictionIds, onChangeSelectedJurisdictionIds };
};

export default useJurisdictionEligibility;
